import { destroyFormCooperator, getFormCooperator, getFormCooperators, updateFormCooperator } from '@/lib/api/formCooperatorApi'
import {
  DestroyFormCooperatorMutationVariables,
  GetFormCooperatorQuery,
  GetFormCooperatorsQuery,
  UpdateFormCooperatorMutationVariables,
} from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { QueryState } from './queryStateType'

export type ShallowFormCooperator = NonNullable<
  NonNullable<NonNullable<NonNullable<GetFormCooperatorsQuery['form']>['cooperators']>['nodes']>
>[number] & {
  formToken: string
}

type StateType = {
  queryState: {
    [formToken: string]: QueryState & { endCursor?: string | null; hasNextPage?: boolean; totalCount?: number }
  }
}

const initialState: StateType = { queryState: {} }

const formCooperatorAdapter = createEntityAdapter<ShallowFormCooperator, string>({
  selectId: model => `${model.formToken}:${model.id}`,
})

export const formCooperatorSlice = createSlice({
  name: 'formCooperators',
  initialState: formCooperatorAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFormCooperatorsThunk.pending, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken].isLoading = true
        } else {
          state.queryState[formToken] = {
            isLoading: true,
            loaded: false,
            error: null,
          }
        }
      })
      .addCase(fetchFormCooperatorsThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken].loaded = true
          state.queryState[formToken].isLoading = false
        }
        const nodes = action.payload.cooperators?.nodes?.map(node => ({
          ...node,
          formToken,
        }))
        if (action.meta.arg.reset) {
          formCooperatorAdapter.removeMany(
            state,
            Object.values(state.entities)
              .filter(cooperator => cooperator.formToken === formToken)
              .map(cooperator => `${formToken}:${cooperator.id}`)
          )
        }
        formCooperatorAdapter.upsertMany(state, nodes as ShallowFormCooperator[])
        if (state.queryState[formToken]) {
          state.queryState[formToken].endCursor = action.payload.cooperators?.pageInfo?.endCursor
          state.queryState[formToken].hasNextPage = action.payload.cooperators?.pageInfo?.hasNextPage
          state.queryState[formToken].totalCount = action.payload.cooperators?.totalCount
        }
      })
      .addCase(fetchFormCooperatorsThunk.rejected, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (state.queryState[formToken]) {
          state.queryState[formToken].isLoading = false
          state.queryState[formToken].error = action.error.message
        }
      })
      .addCase(updateFormCooperatorThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        const cooperatorId = action.meta.arg.targetId
        formCooperatorAdapter.updateOne(state, {
          id: `${formToken}:${cooperatorId}`,
          changes: { formRole: { id: action.meta.arg.roleId } },
        })
      })
      .addCase(destroyFormCooperatorThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        const cooperatorId = action.meta.arg.targetId
        formCooperatorAdapter.removeOne(state, `${formToken}:${cooperatorId}`)
      })
      .addCase(fetchFormCooperatorThunk.fulfilled, (state, action) => {
        const formToken = action.meta.arg.formToken
        if (action.payload) {
          formCooperatorAdapter.upsertOne(state, {
            ...action.payload,
            formToken,
          } as ShallowFormCooperator)
        }
      })
  },
})

export const fetchFormCooperatorsThunk = createAsyncThunk<
  NonNullable<GetFormCooperatorsQuery['form']>,
  { formToken: string; first: number; reset?: boolean },
  { state: RootState }
>('formCooperators/fetchFormCooperators', async (payload, thunkAPI) => {
  const { data } = await getFormCooperators({
    formToken: payload.formToken,
    first: payload.first,
    after: payload.reset ? null : thunkAPI.getState().formCooperators.queryState[payload.formToken]?.endCursor,
  })
  return data?.form?.cooperators?.nodes ? data.form : thunkAPI.rejectWithValue('failed to load formCovers')
})

export const fetchFormCooperatorThunk = createAsyncThunk<
  GetFormCooperatorQuery['cooperator'],
  { formToken: string; cooperatorId: string }
>('formCooperators/fetchFormCooperator', async (payload, thunkAPI) => {
  const { data } = await getFormCooperator({
    formId: payload.formToken,
    id: payload.cooperatorId,
  })
  return data?.cooperator ?? thunkAPI.rejectWithValue('failed to load cooperator')
})

export const updateFormCooperatorThunk = createAsyncThunk<void, UpdateFormCooperatorMutationVariables>(
  'formCooperators/updateFormCooperator',
  async (payload, thunkAPI) => {
    const { data } = await updateFormCooperator(payload)
    if (data?.updateFormCooperator?.errors) {
      return thunkAPI.rejectWithValue(data.updateFormCooperator.errors)
    }
  }
)

export const destroyFormCooperatorThunk = createAsyncThunk<void, DestroyFormCooperatorMutationVariables>(
  'formCooperators/destroyFormCooperator',
  async (payload, thunkAPI) => {
    const { data } = await destroyFormCooperator(payload)
    if (data?.destroyFormCooperator?.errors) {
      return thunkAPI.rejectWithValue(data.destroyFormCooperator.errors)
    }
  }
)

export const formCooperatorSelectors = formCooperatorAdapter.getSelectors()

export const selectFormCooperatorsQueryStateByFormToken = createSelector(
  (state: RootState) => state.formCooperators.queryState,
  (_state: RootState, formToken: string) => formToken,
  (state, formToken) => state[formToken]
)

export const selectFormCooperatorsByFormToken = createSelector(
  (state: RootState) => state.formCooperators,
  (_state: RootState, formToken: string) => formToken,
  (state, formToken) => Object.values(state.entities).filter(cooperator => cooperator.formToken === formToken)
)

export const selectFormCooperatorById = (state: RootState, formToken: string, cooperatorId: string) =>
  state.formCooperators.entities[`${formToken}:${cooperatorId}`]

export default formCooperatorSlice.reducer
